<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link to="/app/users/list" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Edit User</h6>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-8">
              <div v-if="isSuperAdminUser" class="alert alert-warning mb-3">
                <vue-feather type="alert-triangle" size="16" class="me-2" />
                Super Admin accounts cannot be modified via the admin panel. Use direct database access.
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Full Name <span class="text-danger">*</span></label>
                  <input v-model="form.fullName" type="text" class="form-control" required :disabled="isSuperAdminUser" />
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Email</label>
                  <input v-model="form.email" type="email" class="form-control" disabled />
                  <small class="text-muted">Email cannot be changed</small>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Role</label>
                  <input :value="form.role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())" type="text" class="form-control" disabled />
                  <small class="text-muted">Role cannot be changed via API</small>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Status</label>
                  <select v-model="form.isActive" class="form-select" :disabled="isSuperAdminUser">
                    <option :value="true">Active</option>
                    <option :value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <div v-if="error" class="alert alert-danger">{{ error }}</div>

              <div class="d-flex gap-2 pt-2">
                <button type="submit" class="btn btn-primary" :disabled="loading || isSuperAdminUser">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Update User
                </button>
                <router-link to="/app/users/list" class="btn btn-outline-secondary">Cancel</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

// API Base Path
const API_BASE = '/v1/admin'

export default {
  name: 'EditUser',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const authStore = useAuthStore()
    const alert = useAlert()
    const isSuperAdmin = computed(() => authStore.isSuperAdmin)
    const isSuperAdminUser = ref(false)

    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Users', url: '/app/users/list' },
      { title: 'Edit User', active: true }
    ]

    const form = ref({
      fullName: '',
      email: '',
      role: '',
      isActive: true
    })
    const loading = ref(false)
    const error = ref('')

    /**
     * Fetch admin user
     * GET /api/v1/admin/admin-users/{id}
     */
    const fetchUser = async () => {
      try {
        const response = await api.get(`${API_BASE}/admin-users/${route.params.id}`)
        const user = response.data
        isSuperAdminUser.value = user.role === 'super_admin'
        form.value = {
          fullName: user.fullName,
          email: user.email,
          role: user.role,
          isActive: user.isActive
        }
      } catch (err) {
        alert.error('Error', err.response?.data?.error || 'User not found')
        router.push('/app/users/list')
      }
    }

    /**
     * Update admin user
     * PUT /api/v1/admin/admin-users/{id}
     * Note: Super Admin cannot be modified via API
     */
    const handleSubmit = async () => {
      loading.value = true
      error.value = ''

      try {
        await api.put(`${API_BASE}/admin-users/${route.params.id}`, {
          fullName: form.value.fullName,
          isActive: form.value.isActive,
          createdBy: authStore.user?.id
        })
        alert.success('Success', 'User updated successfully')
        router.push('/app/users/list')
      } catch (err) {
        error.value = err.response?.data?.error || 'Failed to update user'
      } finally {
        loading.value = false
      }
    }

    onMounted(fetchUser)

    return { breadcrumbs, form, loading, error, isSuperAdmin, isSuperAdminUser, handleSubmit }
  }
}
</script>
